import { CloseOutlined } from "@ant-design/icons";
import { Flex, Table, Tabs, Tooltip } from "antd";
import classNames from "classnames";
import { dropRightWhile, groupBy, sortBy } from "lodash-es";
import { DragEvent, useMemo } from "react";
import styled from "styled-components";
import { WorkSheet } from "xlsx";

import styles from "./TablePreview.module.scss";

interface TablePreviewProps {
    worksheet: WorkSheet | null;
    sheetOptions?: any;
    sheetName: string;
    dragging: string | null;
    action: (type: string, row: number | null) => void;
    headerRow: number | null;
    dataRow: number | null;
    onSheetChange?: (sheetName: string) => void;
}

export const TablePreview = ({
  sheetOptions,
  sheetName,
  action,
  dragging,
  headerRow,
  dataRow,
  onSheetChange,
  worksheet
}: TablePreviewProps) => {
  const alphabetArray = useMemo(() => {
    return Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));
  }, []);
  // workbook.Sheets.[A1 - ZnN]
  const massagedData = useMemo(() => {
    const sheet = worksheet;
    // filter out !ref, !margin
    // take boundries from !ref e.g. A1:X42
    if (sheet) {
      const [startRow, endRow] = sheet["!ref"]?.match(/\d+/g) || [];
      const cells = Object.entries(sheet).filter(([key, value]) => {
        return !key.startsWith("!");
      });

      const groupdByRow = groupBy(cells, ([key]) => key.replace(/[A-Z]/g, ""));
      const data = Object.entries(groupdByRow).map(([key, values]) => {
        const row = key.replace(/[A-Z]/gi, ""); // Extract numeric portion

        return values.reduce(
          (memo, [k, v]) => {
            const column = k.replace(/[0-9]/g, ""); // Extract alphabet portion
            // @ts-ignore
            memo[column] = v.v;
            return memo;
          },
          { row }
        );
      });

      // Add empty rows if the data is not complete
      if (startRow && endRow) {
        const startRowNum = parseInt(startRow, 10);
        const endRowNum = parseInt(endRow, 10);

        for (let i = startRowNum; i <= endRowNum; i++) {
          if (!data.some((row) => row.row === String(i))) {
            // @ts-ignore
            data.push({ row: String(i) });
          }
        }
      }

      const sortedData = sortBy(data, ({ row }) => parseInt(row, 10));

      // Filter out empty rows at the end of the file
      const filteredData = dropRightWhile(sortedData, ((row) => {
        return Object.keys(row).length < 2 ; // Keep rows with data, don't count the row number
      }));
        
      // Sort by row number
      return filteredData;
    }

    return [];
  }, [worksheet]);

  const data = useMemo(() => {
    const getTitle = (row: string) => {
      if (row === String(headerRow)) {
        return <div><CloseOutlined onClick={() => action("headerRow", null)}/> Header Row</div>;
      } else if (row === String(dataRow)) {
        return <div><CloseOutlined onClick={() => action("dataRow", null)}/> Data row</div>;
      }
      return "";
    }

    const getTooltipColor = (row: string) => {
      if (row === String(headerRow)) {
        return "#e95b00";
      } else if (row === String(dataRow)) {
        return "#52c41a";
      }
      return "#000000";
    }

    return massagedData.map((row) => ({
        ...row, rowIndex: row.row, row: 
        <Tooltip
          open={row.row === String(headerRow) || row.row === String(dataRow)}
          title={getTitle(row.row)}
          placement="left"
          color={getTooltipColor(row.row)}
          key={getTooltipColor(row.row)}
        >
          <div>{row.row}</div>
        </Tooltip>
      }))
  }, [massagedData, headerRow, dataRow]);

  const columns = alphabetArray.map((letter) => {
    return {
      title: letter,
      dataIndex: letter,
      key: letter,
      ellipsis: true,
      width: 150,
    };
  });

  columns.unshift({
    title: "",
    dataIndex: "row",
    key: "row",
    // @ts-ignore
    sortOrder: "ascend",
    width: 40,
  });

  const handleDrop = (event: DragEvent<HTMLTableRowElement>) => {
    event.preventDefault();
    const type = event.dataTransfer.getData("text/plain");
    // @ts-ignore
    const row = event?.target?.parentElement.dataset.row;
    if (row) {
      action(type, row);
    }
  }

  return (
    <Flex
      vertical
      align="flex-start"
      justify="flex-start"
      className={styles.tablePreview}
    >
      <Table
        className={styles.root}
        virtual={true}
        columns={columns}
        dataSource={data}
        rowHoverable={false}
        size="small"
        pagination={false}
        onRow={(record, index) => {
          return {
            onDrop: handleDrop,
            onDragOver: (event) => {
              event.preventDefault();
              // @ts-ignore
              event?.target?.parentElement.classList.add(dragging === "headerRow" ? styles.headers : styles.data);
            },
            onDragLeave: (event) => {
              event.preventDefault();
              if (dragging === "headerRow") {
                // @ts-ignore
                event?.target?.parentElement.classList.remove(styles.headers);
              }
              if (dragging === "dataRow") {
                // @ts-ignore
              event?.target?.parentElement.classList.remove(styles.data);
              }
            }, 
            className: classNames({
              [styles.row]: true,
              [styles.headers]: record.rowIndex === String(headerRow),
              [styles.data]: dataRow && record.rowIndex === String(dataRow)
            }),
            style: {
              cursor: "pointer",
            },
            "data-row": record.rowIndex,
          };
        }}
        onHeaderRow={() => ({
          className: styles.headerRow
        })}
        scroll={{ x: 300, y: 600 }}
      />
      <StyledTabs
        items={sheetOptions}
        type="card"
        defaultActiveKey={sheetOptions[0]?.key}
        activeKey={sheetName}
        onChange={(key) => {
          if (onSheetChange) {
            onSheetChange(key);
          }
        }}
        style={{ transform: "" }}
      />
    </Flex>
  );
};

const StyledTabs = styled(Tabs)(() => `
  border: none;
  border-top: 1px solid #5e5e5e;
  width: 100%;

  .ant-tabs-nav {
    margin-bottom: 0;
    transform: translate(0, -1px);

    &:before {
      display: none;
    }

    .ant-tabs-nav-list {
      .ant-tabs-tab {

        color: black;
        background-color: #cbcbcb;
        border-radius: 0 0 8px 8px;
        border: 1px solid transparent;
        border-top: none;
        transform: translate(0, 1px);

        transition: all 0.3s ease-in-out;
      
        &.ant-tabs-tab-active {
          border: 1px solid #5e5e5e;
          
          border-top: 0;
          background-color: #f1f1f1;
          color: #e95b00;

          transform: translate(0, -1px);
      
          .ant-tabs-tab-btn {
            color: #e95b00;
          }
        }
      }
    }
  }
`);